import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import React, { useState, useEffect, useCallback } from 'react';
import UpdatePrepReport, { PrepReportItemPayload } from './update-prep-report';
import ReviewPrepReport from './review-prep-report';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { EditAltIcon } from 'cokitchen-icons';
import { apiService } from 'services/base.service';
import useDashboardContext from 'contexts/dashboard.context';
import { handleApiError } from 'utils/functions';
import { PrepRequestType } from 'models/prep-request.model';
import { toast } from 'react-toastify';
import useRoleContext from 'contexts/role.context';
import { InventoryModule, RolePermission } from 'models/permission.model';

const PrepReport = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [request, setRequest] = useState({} as PrepRequestType);
  const [reportItems, setReportItems] = useState<PrepReportItemPayload[]>([]);
  const { activeStore } = useDashboardContext();
  const { can } = useRoleContext();
  const { pathname } = useLocation();
  const isPrepTeam = pathname.includes('prep-team');
  const canWriteKitchenRequisition = can(InventoryModule.KitchenRequisition, RolePermission.Write);
  const navigate = useNavigate();
  const { id } = useParams();

  const sendReport = async () => {
    if (!canWriteKitchenRequisition || !activeStore) return;

    try {
      setLoading(true);
      await apiService.put(`prep-kitchen-request/${request.id}/report`, {
        version: 'v2',
        data: {
          items: reportItems
        }
      });
      toast(<ToastMessage message='Request updated successfully' type='success' />);
      if (isPrepTeam) {
        navigate(`/prep-team/prep-team-requests/completed/${id}`);
      } else {
        navigate(`/main-store/brand-chefs/manage-requests/complete/${id}`);
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const onConfirm = () => {
    if (!canWriteKitchenRequisition) return;
    if (step === 1) {
      setStep(2);
    } else {
      sendReport();
    }
  };

  const getRequestItems = async () => {
    if (!activeStore) return;
    setLoading(true);
    try {
      const res = await apiService.get(`prep-kitchen-request/${id}`, {
        version: 'v2'
      });
      setRequest(res.data?.data);
    } catch (e) {
      handleApiError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getRequestItems();
  }, []);

  const updatePrepReport = useCallback((items: PrepReportItemPayload[]) => {
    setReportItems(items);

    const reportItemsById = items.reduce((acc, item) => {
      acc[item.prep_request_item_id] = item;
      return acc;
    }, {} as Record<string, PrepReportItemPayload>);

    const totalsByPrepIngredientId = items.reduce((acc, item) => {
      item.batches.forEach((batch) => {
        const totals = acc[batch.prep_ingredient_id] ?? {
          excess_quantity: 0,
          quantity_wasted: 0
        };
        totals.excess_quantity += Number(batch.excess_quantity || 0);
        totals.quantity_wasted += Number(batch.quantity_wasted || 0);
        acc[batch.prep_ingredient_id] = totals;
      });
      return acc;
    }, {} as Record<string, { excess_quantity: number; quantity_wasted: number }>);

    setRequest((prev) => ({
      ...prev,
      items: prev.items?.map((reqItem) => {
        const reportItem = reportItemsById[reqItem.id];
        return {
          ...reqItem,
          quantity_produced: reportItem?.quantity_produced ?? reqItem.quantity_produced,
          prep_item_ingredients: reqItem.prep_item_ingredients?.map((prepItem) => {
            const totals = totalsByPrepIngredientId[prepItem.id];
            if (!totals) return prepItem;
            return {
              ...prepItem,
              excess_quantity: totals.excess_quantity,
              quantity_wasted: totals.quantity_wasted
            };
          })
        };
      })
    }));
  }, []);
  return (
    <div className='ml-2 md:ml-8'>
      <div className='flex items-center'>
        <h4 className='text-[24px] font-semibold text-[#2E3031] mr-2'>Request #{request.code}</h4>
      </div>
      <p className='text-[#6F7376] text-sm mb-8'>Track progress with procurement</p>

      <div className='border-4 border-[#F7F7F8] rounded py-[24px]  md:max-w-[670px]'>
        <div className='w-full'>
          <div className='flex justify-between items-center px-[28px]'>
            <div className='pb-2'>
              <p className='font-semibold text-[20px]'>
                {step === 1 ? 'Provide report details' : 'Review report details'}
              </p>
              <p className='text-[#6F7376] text-sm'>Ensure you fill the fields as necessary</p>
            </div>
            {step === 2 && (
              <EditAltIcon
                className='cursor-pointer'
                onClick={() => {
                  setStep(1);
                }}
              />
            )}{' '}
          </div>
          {loading ? (
            <p className='text-center py-10'>Loading report details...</p>
          ) : step === 1 ? (
            <UpdatePrepReport onUpdateItems={updatePrepReport} request={request} />
          ) : (
            <ReviewPrepReport request={request} />
          )}

          <div>
            <hr className='border-t-4 border-[#F7F7F8]' />
            <div className='flex gap-6 items-center justify-end mt-6 mx-[28px] ml-auto'>
              <Button variant='link' color='gray' small onClick={() => navigate(-1)}>
                Cancel
              </Button>
              {canWriteKitchenRequisition && (
                <Button
                  variant='block'
                  color='primary'
                  small
                  className='w-[153px] h-[43px]'
                  onClick={onConfirm}
                  loading={loading}>
                  {step === 1 ? 'Continue' : 'Confirm'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrepReport;
